import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useAppState } from "Context/StateContext";
import { useTranslation } from "react-i18next";
import { Box, IconButton, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { PushPin, PushPinOutlined } from "@mui/icons-material";
import { useLocation } from "react-router-dom";

import { menuConfigMain } from "./config/menuConfigMain";
import { menuConfigSettings } from "./config/menuConfigSettings";

import { MenuItem, SidebarProps } from "../SideBar/types";
import { getNode, filterAndPatchMenu } from "../SideBar/utils";
import {
  COLLAPSED_WIDTH,
  COLLAPSED_WIDTH_PX,
  EXPANDED_WIDTH,
  EXPANDED_WIDTH_PX,
  ROW_H,
  SUBMENU_WIDTH,
  SUBMENU_WIDTH_PX,
} from "../SideBar/constants";

import IconsBar from "../SideBar/IconsBar";
import FirstLevelList from "../SideBar/FirstLevel";
import SecondLevelList from "../SideBar/SecondLevel";
import ThirdLevelList from "../SideBar/ThirdLevel";

import SvgIconUrl from "../../helpers/SvgIcon/SvgIconUrl";
import { AppConfig } from "Constants";
import { useResourcePermissionsActions } from "./useResourcePermissionsActions";
import { getBrandCustom } from "Util/getCustomColor";
import { IconSidebar } from "Components/Generic/Icons/IconSidebar";

import {
  CompanyUserAndEmailWrapper,
  Divider,
  DividerIcons,
  SidebarContainer,
  StyledDrawer,
  UserProfileImage,
  UserProfileMenuHeader,
  UserProfileName,
} from "./styles";

const ShipeeziIcon = require("Assets/img/appLogo.png");
const SIDEBARICON = require("Assets/icons/arrow-right-4.svg");
const SETTINGSICON = require("Assets/icons/setting.svg");
const COMPANIESICON = require("Assets/icons/companies.svg");
const HELPICON = require("Assets/icons/exclamation.svg");
const LOGO = AppConfig.appLogo;

export default function Sidebar({ onPinnedChange }: SidebarProps) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [activeFirstLevel, setActiveFirstLevel] = useState<string | null>(null);
  const [openDrawerKey, setOpenDrawerKey] = useState<string | null>(null);
  const [openThirdKey, setOpenThirdKey] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);
  const [isHoveringDrawer, setIsHoveringDrawer] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [secondAsPrimary, setSecondAsPrimary] = useState<boolean>(false);

  const { t } = useTranslation();
  const { currentUser } = useAppState();
  const { navBarLinks, showButtonNetwork } = useResourcePermissionsActions();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const resetSidebar = useCallback(() => {
    setSecondAsPrimary(false);
    setExpanded(false);
    setActiveFirstLevel(null);
    setOpenDrawerKey(null);
    setOpenThirdKey(null);
    setActiveKey(null);
  }, []);

  const openFirstLevel = useCallback(
    (key: string, { asDrawer = false }: { asDrawer?: boolean } = {}) => {
      setSecondAsPrimary(false);
      setExpanded(true);
      setActiveFirstLevel(key);
      setActiveKey(key);
      setOpenThirdKey(null);
      setOpenDrawerKey(asDrawer ? key : null);
    },
    []
  );

  const avatarUrl = useMemo(
    () => currentUser?.picture?.mediaLink ?? require("Assets/profile.png"),
    [currentUser?.picture?.mediaLink]
  );

  const firstLevelOpen = useMemo(
    () => pinned || expanded || !!activeFirstLevel,
    [pinned, expanded, activeFirstLevel]
  );

  const baseOffsetLeft = useMemo(() => (pinned ? EXPANDED_WIDTH_PX : COLLAPSED_WIDTH_PX), [pinned]);

  const secondLevelLeft = useMemo(() => {
    return secondAsPrimary
      ? pinned
        ? 0
        : COLLAPSED_WIDTH_PX
      : baseOffsetLeft + (pinned ? 0 : expanded ? EXPANDED_WIDTH_PX : 0);
  }, [secondAsPrimary, pinned, baseOffsetLeft, expanded]);

  const thirdLevelLeft = useMemo(() => secondLevelLeft + SUBMENU_WIDTH_PX, [secondLevelLeft]);

  const filteredMain = useMemo(
    () =>
      filterAndPatchMenu(menuConfigMain, navBarLinks || {}, { forceNetwork: showButtonNetwork }),
    [navBarLinks, showButtonNetwork]
  );

  const filteredSettings = useMemo(
    () =>
      filterAndPatchMenu(menuConfigSettings, navBarLinks || {}, {
        isMilitary: Boolean(currentUser?.company?.isMilitary),
      }),
    [navBarLinks, currentUser?.company?.isMilitary]
  );

  const resolveNode = useCallback(
    (key: string | null): MenuItem | null => {
      if (!key) return null;
      if (key === "profile" || key === "company") return { key, label: key } as MenuItem;
      return getNode(key, [filteredMain, filteredSettings]);
    },
    [filteredMain, filteredSettings]
  );

  const drawerNode: MenuItem | null = useMemo(
    () => resolveNode(openDrawerKey),
    [openDrawerKey, resolveNode]
  );
  const thirdNode: MenuItem | null = useMemo(
    () => resolveNode(openThirdKey),
    [openThirdKey, resolveNode]
  );

  const headerSx = { height: 56, px: 2, flexShrink: 0 } as const;

  useEffect(() => {
    setPinned(localStorage.getItem("sidebar_pinned") === "true");
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebar_pinned", String(pinned));
    onPinnedChange?.(pinned);
  }, [pinned, onPinnedChange]);

  useEffect(() => {
    if (!pinned) {
      resetSidebar();
    }
  }, [location.pathname, pinned]);

  const handleDocumentClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (target.closest(".MuiDrawer-paper") || target.closest("[data-sidebar]")) {
        return;
      }

      if (target.closest('[data-filter-menu="true"]')) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(target as Node)) {
        resetSidebar();
      }
    },
    [resetSidebar]
  );

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [handleDocumentClick]);

  const handleAvatarClick = useCallback(() => {
    setSecondAsPrimary(true);
    setExpanded(false);
    setActiveFirstLevel(null);
    setOpenDrawerKey("profile");
    setOpenThirdKey(null);
    setActiveKey("profile");
  }, []);

  const handleLogoClick = useCallback(() => {
    resetSidebar();
  }, [resetSidebar]);

  const handleHoverIcon = useCallback(
    (key: string) => {
      if (key === "company" || key === "profile") return;
      if (secondAsPrimary) {
        openFirstLevel(key, { asDrawer: key === "settings" });
        return;
      }
      if (!pinned) {
        openFirstLevel(key, { asDrawer: key === "settings" });
      }
    },
    [pinned, secondAsPrimary, openFirstLevel]
  );

  const firstListRef = useRef<HTMLDivElement | null>(null);

  const FirstGroup: React.FC<{ items: MenuItem[] }> = useCallback(
    ({ items }) => {
      const hasSettings = items.some((it) => it.key === "settings");
      return (
        <Box
          ref={firstListRef}
          sx={{
            display: "block",
            overflow: "hidden",
            // removing for now. If causes overflow issues, then I tried another way
            // maxHeight: hasSettings ? "auto" : "calc(100dvh - 56px - 160px)",
            WebkitOverflowScrolling: hasSettings ? undefined : "touch",
          }}
        >
          <FirstLevelList
            items={items}
            activeFirstLevel={activeFirstLevel}
            setActiveFirstLevel={setActiveFirstLevel}
            setOpenDrawerKey={setOpenDrawerKey}
            setOpenThirdKey={setOpenThirdKey}
            onSelectTopIcon={setActiveKey}
            onRequestClose={resetSidebar}
          />
        </Box>
      );
    },
    [activeFirstLevel, resetSidebar]
  );

  const iconsBarItems = useMemo(() => {
    const items = [...filteredMain];
    if (filteredSettings.length > 0) {
      items.push({ key: "settings", label: "Settings", icon: SETTINGSICON } as any);
    }
    return items;
  }, [filteredMain, filteredSettings]);

  return (
    <SidebarContainer ref={containerRef}>
      <IconsBar
        items={iconsBarItems}
        pinned={pinned}
        sidebarIcon={
          firstLevelOpen ? (
            <IconSidebar color={getBrandCustom("darkPrimaryColor", "#E4E7EC")} />
          ) : null
        }
        logoSrc={currentUser?.company?.icon?.mediaLink || ShipeeziIcon}
        width={COLLAPSED_WIDTH_PX}
        show={!pinned}
        avatarUrl={avatarUrl}
        activeKey={activeKey}
        onHoverIcon={handleHoverIcon}
        onAvatarClick={handleAvatarClick}
        onLogoClick={handleLogoClick}
        bottomActions={[
          {
            key: "companies",
            icon: require("Assets/icons/companies.svg"),
            onClick: () => {
              setSecondAsPrimary(true);
              setExpanded(false);
              setActiveFirstLevel(null);
              setOpenDrawerKey("company");
              setOpenThirdKey(null);
              setActiveKey("company");
            },
          },
          {
            key: "help",
            icon: require("Assets/icons/help.svg"),
            onClick: () => window.open("https://help.shipeezi.com/", "_blank"),
          },
        ]}
      />

      <StyledDrawer
        variant={pinned ? "persistent" : "permanent"}
        PaperProps={{
          sx: pinned
            ? {
                position: "fixed",
                top: 0,
                left: 0,
                width: EXPANDED_WIDTH_PX,
                height: "100vh",
                boxShadow: "none",
                borderRight: "1px solid #E5E7EB",
                zIndex: 1100,
                transition: "width 0.3s ease",
                display: "flex",
                flexDirection: "column",
                overflowAnchor: "none",
                overflowX: "hidden",
                overflowY: "auto",
              }
            : {
                position: "fixed",
                top: 0,
                left: COLLAPSED_WIDTH,
                width: expanded ? EXPANDED_WIDTH_PX : 0,
                height: "100vh",
                boxShadow: "none",
                borderRight: "1px solid #E5E7EB",
                zIndex: 1104,
                transition: "width 0.3s ease",
                display: "flex",
                flexDirection: "column",
                overflowAnchor: "none",
                overflowY: "auto",
                overflowX: "hidden",
              },
          "data-sidebar": "first",
        }}
        open={pinned || expanded}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={headerSx}>
          <img
            src={
              pinned || !isHoveringDrawer || openDrawerKey || openThirdKey
                ? currentUser?.company?.logo?.mediaLink || LOGO
                : currentUser?.company?.icon?.mediaLink || ShipeeziIcon
            }
            alt="Shipeezi Logo"
            height={25}
          />
          <IconButton size="small" onClick={() => setPinned((p) => !p)}>
            {pinned ? (
              <PushPin fontSize="small" color="primary" />
            ) : (
              <PushPinOutlined fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <FirstGroup items={filteredMain} />
          <Divider sx={{ my: 1 }} />
          <FirstGroup items={filteredSettings} />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            paddingLeft: "16px",
            paddingRight: "16px",
            paddingBottom: "10px",
            flexShrink: 0,
          }}
        >
          <ListItemButton
            selected={openDrawerKey === "company" || activeFirstLevel === "company"}
            onClick={() => {
              setActiveFirstLevel("company");
              setOpenDrawerKey("company");
              setOpenThirdKey(null);
              setActiveKey("profile");
            }}
            sx={{
              borderRadius: 1,
              height: ROW_H,
              fontFamily: "Manrope",
              fontWeight: 600,
              fontSize: 15,
              color:
                openDrawerKey === "company" || activeFirstLevel === "company"
                  ? getBrandCustom("primaryColor", "#2563EB")
                  : "#475467",
              "&.Mui-selected": {
                backgroundColor: "#F1F6FB",
                color: getBrandCustom("primaryColor", "#2563EB"),
              },
              "&:hover": {
                backgroundColor: getBrandCustom("headerBackgroundColor", "#F2F4F7"),
                color: getBrandCustom("primaryColor", "#1D2939"),
              },
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 1,
            }}
          >
            <ListItemIcon sx={{ minWidth: 17, color: "inherit", alignItems: "center" }}>
              <SvgIconUrl src={COMPANIESICON} size={17} />
            </ListItemIcon>
            <ListItemText primary={currentUser?.company?.name ?? t("unknown")} />
          </ListItemButton>

          <ListItemButton
            onClick={() => window.open("https://help.shipeezi.com/", "_blank")}
            sx={{
              borderRadius: 1,
              height: ROW_H,
              fontFamily: "Manrope",
              fontWeight: 600,
              fontSize: 15,
              color: "#475467",
              "&:hover": {
                backgroundColor: getBrandCustom("headerBackgroundColor", "#F2F4F7"),
                color: getBrandCustom("primaryColor", "#1D2939"),
              },
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 1,
            }}
          >
            <ListItemIcon sx={{ minWidth: 17, color: "inherit", alignItems: "center" }}>
              <SvgIconUrl src={HELPICON} size={17} />
            </ListItemIcon>
            <ListItemText primary={t("help")} />
          </ListItemButton>

          <DividerIcons
            sx={{ marginTop: "8px", marginBottom: "8px", background: "#E2E8F0", width: "95%" }}
          />

          <UserProfileMenuHeader
            container
            alignItems="center"
            onClick={() => {
              setActiveFirstLevel("profile");
              setOpenDrawerKey("profile");
              setOpenThirdKey(null);
              setActiveKey("profile");
            }}
            sx={{ cursor: "pointer" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
              <UserProfileImage src={avatarUrl} alt="user profile" />
              <CompanyUserAndEmailWrapper>
                <UserProfileName>{currentUser?.name ?? t("unknown")}</UserProfileName>
              </CompanyUserAndEmailWrapper>
              <IconButton size="small" sx={{ ml: "auto", color: "#64748B" }}>
                <Box
                  component="span"
                  sx={{
                    width: 17,
                    height: 17,
                    display: "block",
                    bgcolor: "currentColor",
                    mask: `url(${require("Assets/icons/arrow-right.svg")}) no-repeat center`,
                    maskSize: "contain",
                    WebkitMask: `url(${require("Assets/icons/arrow-right.svg")}) no-repeat center`,
                    WebkitMaskSize: "contain",
                  }}
                />
              </IconButton>
            </Box>
          </UserProfileMenuHeader>
        </Box>
      </StyledDrawer>

      <SecondLevelList
        openNode={drawerNode}
        pinned={pinned}
        left={secondLevelLeft}
        onClose={() => {
          setSecondAsPrimary(false);
          setOpenDrawerKey(null);
          setOpenThirdKey(null);
          setActiveKey(null);
        }}
        onHoverChange={setIsHoveringDrawer}
        onOpenThird={setOpenThirdKey}
        onRequestClose={resetSidebar}
      />

      <ThirdLevelList
        openNode={thirdNode}
        pinned={pinned}
        left={thirdLevelLeft}
        onClose={() => setOpenThirdKey(null)}
        onRequestClose={resetSidebar}
      />
    </SidebarContainer>
  );
}
